import { useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Check, Target, Eye, Sparkles } from "lucide-react";
import { LOGO_URL } from "../lib/api";

const FINDS = [
  "200+ Curated Business Opportunities",
  "Industry-Based Startup Categories",
  "AI-Ready Builder Prompts",
  "Premium Startup Inspiration",
  "Ready-to-Build Applications",
  "Startup Notebook",
  "Founder Resources",
  "Future AI Builder Integrations",
];

export const AboutModal = ({ open, onClose }) => {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-[#0F0F0F]/55 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          data-testid="about-overlay"
        >
          <motion.div
            className="bg-white rounded-3xl w-full max-w-2xl shadow-2xl relative max-h-[92vh] overflow-y-auto"
            initial={{ scale: 0.95, opacity: 0, y: 12 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.95, opacity: 0, y: 12 }}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            data-testid="about-modal"
          >
            <button
              onClick={onClose}
              className="absolute top-5 right-5 text-white/70 hover:text-white transition-colors z-10"
              data-testid="about-close-x"
            >
              <X size={22} />
            </button>

            {/* header */}
            <div className="bg-[#0F0F0F] px-8 pt-10 pb-12 text-center relative overflow-hidden">
              <div
                className="absolute inset-0 opacity-60"
                style={{ background: "radial-gradient(circle at 50% 20%, rgba(212,175,55,0.22) 0%, rgba(212,175,55,0) 55%)" }}
              />
              <div className="relative">
                <img src={LOGO_URL} alt="LaunchBS" className="h-16 w-16 object-contain mx-auto mb-4 drop-shadow-[0_10px_30px_rgba(212,175,55,0.4)]" />
                <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] font-semibold">About LaunchBS</p>
                <h2 className="font-display text-3xl font-extrabold text-white mt-2">
                  Discover. <span className="text-[#D4AF37]">Inspire.</span> Build.
                </h2>
              </div>
            </div>

            <div className="px-8 py-8 space-y-8">
              <div className="space-y-4 text-sm text-[#4B5563] leading-relaxed">
                <p>
                  <span className="font-semibold text-[#0F0F0F]">LaunchBS</span> is a premium platform
                  designed to help entrepreneurs discover business opportunities worth building.
                </p>
                <p>
                  Whether you're a first-time founder, developer, creator, or entrepreneur, LaunchBS
                  provides a curated collection of startup concepts across industries including AI,
                  technology, finance, healthcare, education, agriculture, real estate, entertainment,
                  and more.
                </p>
                <p>
                  Every business idea is professionally structured with its purpose, target audience,
                  revenue model, core features, market opportunity, and an AI-ready Builder Prompt —
                  helping transform inspiration into execution.
                </p>
                <p>
                  Unlike traditional idea websites, LaunchBS doesn't encourage copying businesses — it
                  encourages creating better ones. Each concept is designed to spark creativity, solve
                  real-world problems, and inspire entrepreneurs to build something uniquely their own.
                </p>
                <p>
                  For those ready to move beyond inspiration, LaunchBS also offers professionally
                  developed applications available for purchase, along with Builder Prompts that
                  integrate with modern AI development platforms to accelerate the journey from concept
                  to product.
                </p>
              </div>

              <Section icon={Target} title="Our Mission">
                <p className="text-sm text-[#4B5563] leading-relaxed">
                  To become the world's leading platform where entrepreneurs discover opportunities,
                  validate ideas, and confidently build the next generation of businesses.
                </p>
              </Section>

              <Section icon={Sparkles} title="What You'll Find">
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  {FINDS.map((f) => (
                    <li key={f} className="flex items-center gap-2.5 text-sm text-[#0F0F0F]">
                      <span className="w-5 h-5 rounded-full bg-[#D4AF37]/15 text-[#D4AF37] flex items-center justify-center shrink-0">
                        <Check size={11} strokeWidth={3} />
                      </span>
                      {f}
                    </li>
                  ))}
                </ul>
              </Section>

              <Section icon={Eye} title="Our Vision">
                <p className="text-sm text-[#4B5563] leading-relaxed">
                  We believe that great businesses begin with great ideas — but finding the right idea
                  is often the hardest part. LaunchBS exists to remove that barrier by giving
                  entrepreneurs access to a growing library of high-quality business opportunities
                  designed to inspire innovation, accelerate execution, and help founders build
                  businesses that make a lasting impact.
                </p>
              </Section>

              <div className="rounded-2xl bg-[#F7F7F7] px-6 py-6 text-center">
                <p className="text-xs uppercase tracking-wider text-[#687280] font-semibold mb-2">
                  LaunchBS Motto
                </p>
                <p className="font-display text-lg font-bold text-[#0F0F0F]">
                  Discover Opportunities. <span className="text-[#D4AF37]">Inspire Innovation.</span> Build the Future.
                </p>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const Section = ({ icon: Icon, title, children }) => (
  <div>
    <div className="flex items-center gap-2 mb-3">
      <Icon size={18} className="text-[#D4AF37]" />
      <h3 className="font-display text-lg font-bold text-[#0F0F0F]">{title}</h3>
    </div>
    {children}
  </div>
);
